import React, { FC, useState } from "react";

import log from "loglevel";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, InputAdornment } from "@mui/material";

import { EmailIcon } from "@/assets/icons";
import { ItsMeButton, OrSeparator, TextInput } from "@/components/atoms";
import { userApi } from "@/shared/apis";

import { EmailFormType, emailValidationSchema } from "../../../shared";
import { StyledAuthForm } from "./styled";

type LoginCheckEmailProps = {
    email: string;
    handleItsmeLogin: () => void;
    onChangeEmailStatus: (status: MyBrokerApi.UserStatus, email: string) => void;
};

const defaultValues = (email: string) => {
    return { email };
};

const LoginCheckEmail: FC<LoginCheckEmailProps> = ({ email = "", handleItsmeLogin, onChangeEmailStatus }) => {
    const { t } = useTranslation();
    const [emailToCheckStatus, setEmailToCheckStatus] = useState("");

    const { handleSubmit, control } = useForm<EmailFormType>({
        defaultValues: defaultValues(email),
        resolver: yupResolver(emailValidationSchema),
    });

    const { isLoading } = useQuery(
        ["getUserStatus", { emailToCheckStatus }],
        () => userApi.getUserStatus(emailToCheckStatus),
        {
            onSuccess: (result) => {
                onChangeEmailStatus(result, emailToCheckStatus);
            },
            enabled: !!emailToCheckStatus,
            cacheTime: 0,
            suspense: false,
        },
    );

    const onSubmit = (data: EmailFormType) => {
        log.info("Start login with normal flow");
        const enteredEmail = data.email.toLowerCase();
        setEmailToCheckStatus(enteredEmail);
    };

    return (
        <StyledAuthForm onSubmit={handleSubmit(onSubmit)}>
            <h2>{t("Login.AccessClientZone")}</h2>
            <span className="login-desc-free">{t("Login.FreeAndSimple")}</span>
            <div>
                <ItsMeButton
                    label={t("ItsMe.ItsMeLabel")}
                    dataTestId="its_me_button-button-login"
                    onClick={handleItsmeLogin}
                />
                <OrSeparator />
            </div>
            <TextInput
                control={control}
                label={t("Login.CheckEmail.YourEmail")}
                autoFocus
                name="email"
                autoComplete="email"
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <EmailIcon />
                        </InputAdornment>
                    ),
                }}
            />
            <Button
                type="submit"
                data-testid="login_check_email-button-submit"
                fullWidth
                className="mt40"
                disabled={isLoading}
            >
                {t("Login.CheckEmail.AccessMyBroker")}
            </Button>
        </StyledAuthForm>
    );
};

export default LoginCheckEmail;
